/**
 * GIWAHS — Auth Client (mode-switch: Supabase | mock)
 * ---------------------------------------------------------------------
 * Mirrors the dataClient pattern: when REACT_APP_SUPABASE_URL +
 * REACT_APP_SUPABASE_ANON_KEY are present, the client wraps Supabase
 * Auth. Otherwise it runs a dev-only mock backed by localStorage so the
 * buyer UX is fully testable without a backend.
 *
 * The same Supabase client instance is reused for data calls
 * (see /lib/supabaseClient.js) — this guarantees auth.uid() lines up
 * with RLS policies in Supabase mode.
 *
 * UI components should NEVER import this file directly — use the
 * useAuth() hook from /context/AuthContext.jsx.
 */
import { getSupabaseClient, isSupabaseConfigured } from './supabaseClient';

const resetRedirectUrl = () => {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base.replace(/\/$/, '')}/reset-password`;
};

// ─── localStorage helpers for mock mode ────────────────────────────────
const MOCK_USERS_KEY    = 'giwahs_mock_auth_users';
const MOCK_SESSION_KEY  = 'giwahs_mock_auth_session';
const MOCK_RESET_KEY    = 'giwahs_mock_auth_reset_email';

const readJSON = (key, fallback) => {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; }
  catch { return fallback; }
};
const writeJSON = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch {} };
const removeKey = (key) => { try { localStorage.removeItem(key); } catch {} };

const newId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `usr_${crypto.randomUUID()}`;
  return `usr_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
};

// Very simple obfuscation — dev mode only, NEVER used in production paths.
const hashish = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; }
  return `mh_${(h >>> 0).toString(36)}`;
};

// ─── Direct REST helper — XHR-based to avoid preview-env fetch tracers
// that pre-read fetch() response bodies and break .json()/.text() in v2.106.x
const restCall = (path, body) => new Promise((resolve) => {
  const url  = process.env.REACT_APP_SUPABASE_URL;
  const anon = process.env.REACT_APP_SUPABASE_ANON_KEY;
  try {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${url}${path}`, true);
    xhr.setRequestHeader('apikey', anon);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.timeout = 20000;
    xhr.onload  = () => {
      let data = {};
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; } catch { /* keep raw */ }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data, raw: xhr.responseText || '' });
    };
    xhr.onerror   = () => resolve({ ok: false, status: 0, data: {}, raw: '', networkError: true });
    xhr.ontimeout = () => resolve({ ok: false, status: 0, data: {}, raw: '', networkError: true });
    xhr.send(JSON.stringify(body));
  } catch {
    resolve({ ok: false, status: 0, data: {}, raw: '', networkError: true });
  }
});

// ─── Subscribers (mock mode) ───────────────────────────────────────────
const mockListeners = new Set();
const emitMock = (event, session) => mockListeners.forEach(cb => { try { cb(event, session); } catch {} });

const makeMockSession = (user) => user ? ({
  user,
  access_token: `mock_token_${user.id}`,
  refresh_token: `mock_refresh_${user.id}`,
  expires_at: Math.floor(Date.now() / 1000) + 3600,
}) : null;

// ─── Mock client ──────────────────────────────────────────────────────
const mockAuth = {
  type: 'mock',

  async getSession() {
    const session = readJSON(MOCK_SESSION_KEY, null);
    return session;
  },

  async signUp({ email, password }) {
    const users = readJSON(MOCK_USERS_KEY, []);
    const normalized = (email || '').trim().toLowerCase();
    if (!normalized || !password) throw new Error('Email and password are required.');
    if (password.length < 6) throw new Error('Password must be at least 6 characters.');
    if (users.some(u => u.email === normalized)) throw new Error('An account with this email already exists.');
    const user = {
      id: newId(),
      email: normalized,
      account_type: 'buyer',
      created_at: new Date().toISOString(),
    };
    users.push({ ...user, password_hash: hashish(password) });
    writeJSON(MOCK_USERS_KEY, users);
    const session = makeMockSession(user);
    writeJSON(MOCK_SESSION_KEY, session);
    emitMock('SIGNED_IN', session);
    return { user, session, needsConfirmation: false };
  },

  async signIn({ email, password }) {
    const users = readJSON(MOCK_USERS_KEY, []);
    const normalized = (email || '').trim().toLowerCase();
    const record = users.find(u => u.email === normalized);
    if (!record || record.password_hash !== hashish(password || '')) {
      throw new Error('Invalid email or password.');
    }
    const user = { id: record.id, email: record.email, account_type: record.account_type || 'buyer', created_at: record.created_at };
    const session = makeMockSession(user);
    writeJSON(MOCK_SESSION_KEY, session);
    emitMock('SIGNED_IN', session);
    return { user, session };
  },

  async signOut() {
    removeKey(MOCK_SESSION_KEY);
    emitMock('SIGNED_OUT', null);
  },

  async requestPasswordReset(email) {
    const users = readJSON(MOCK_USERS_KEY, []);
    const normalized = (email || '').trim().toLowerCase();
    const exists = users.some(u => u.email === normalized);
    // Always succeed (do not leak account existence)
    if (exists) writeJSON(MOCK_RESET_KEY, normalized);
    // Simulate a redirect link by leaving a console hint in dev
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.info('[GIWAHS mock auth] Password-reset requested for', normalized, '— in mock mode, open /reset-password directly to set a new password.');
    }
  },

  /**
   * In mock mode there is no email link — instead, we let the
   * /reset-password page (which is the user navigating to it manually)
   * verify the email pending the reset and set a new password.
   */
  async updatePasswordWithMockToken({ email, password }) {
    const users = readJSON(MOCK_USERS_KEY, []);
    const normalized = (email || '').trim().toLowerCase();
    const pending = readJSON(MOCK_RESET_KEY, null);
    if (!pending || pending !== normalized) throw new Error('No password reset request found for this email.');
    if (!password || password.length < 6) throw new Error('Password must be at least 6 characters.');
    const idx = users.findIndex(u => u.email === normalized);
    if (idx === -1) throw new Error('Account not found.');
    users[idx] = { ...users[idx], password_hash: hashish(password), updated_at: new Date().toISOString() };
    writeJSON(MOCK_USERS_KEY, users);
    removeKey(MOCK_RESET_KEY);
    // Sign user in after reset.
    const user = { id: users[idx].id, email: users[idx].email, account_type: users[idx].account_type || 'buyer', created_at: users[idx].created_at };
    const session = makeMockSession(user);
    writeJSON(MOCK_SESSION_KEY, session);
    emitMock('SIGNED_IN', session);
    return { user, session };
  },

  async updatePassword(newPassword) {
    const session = readJSON(MOCK_SESSION_KEY, null);
    if (!session?.user) throw new Error('You must be signed in to update your password.');
    return this.updatePasswordWithMockToken({ email: session.user.email, password: newPassword });
  },

  async resendConfirmation(/* email */) {
    // Mock mode has no email confirmation step — succeed silently so the
    // UI can fire the analytics event without showing an error.
    return { ok: true, skipped: true };
  },

  onAuthStateChange(callback) {
    mockListeners.add(callback);
    return () => mockListeners.delete(callback);
  },
};

// ─── Supabase client wrapper ──────────────────────────────────────────
const buildSupabaseAuth = () => {
  const supabase = getSupabaseClient();
  if (!supabase) return null;

  return {
    type: 'supabase',

    async getSession() {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return data.session;
    },

    async signUp({ email, password }) {
      const redir = resetRedirectUrl().replace('/reset-password', '/login');
      const result = await restCall('/auth/v1/signup', {
        email, password,
        data: { account_type: 'buyer' },
        options: { emailRedirectTo: redir, data: { account_type: 'buyer' } },
      });

      if (result.networkError) {
        throw new Error('Network error — could not reach Supabase. Check your internet connection.');
      }
      if (!result.ok) {
        const code = result.data?.error_code || result.data?.code || '';
        const msg  = result.data?.msg || result.data?.message || result.data?.error_description || '';
        if (result.status === 422 || code === 'user_already_exists' || /already registered/i.test(msg)) {
          const err = new Error('An account with this email already exists. Try signing in instead — or use "Forgot password" to reset it.');
          err.code = 'user_already_exists';
          throw err;
        }
        if (code === 'weak_password' || /password/i.test(msg)) {
          throw new Error(msg || 'Password does not meet the strength requirements.');
        }
        if (result.status === 429) throw new Error('Too many sign-up attempts. Try again in a minute.');
        throw new Error(msg || `Sign up failed (HTTP ${result.status}).`);
      }

      const data = result.data;
      if (data?.access_token && data?.refresh_token) {
        try { await supabase.auth.setSession({ access_token: data.access_token, refresh_token: data.refresh_token }); }
        catch { /* non-fatal */ }
      }
      return { user: data.user || null, session: data.access_token ? data : null, needsConfirmation: !data.access_token };
    },

    async signIn({ email, password }) {
      const result = await restCall('/auth/v1/token?grant_type=password', { email, password });
      if (result.networkError) throw new Error('Network error — could not reach Supabase.');
      if (!result.ok) {
        const code = result.data?.error_code || result.data?.code || '';
        const msg  = result.data?.msg || result.data?.error_description || result.data?.message || '';
        if (code === 'invalid_credentials' || /invalid login/i.test(msg)) {
          throw new Error('Invalid email or password.');
        }
        if (code === 'email_not_confirmed' || /confirm/i.test(msg)) {
          throw new Error('Please confirm your email before signing in.');
        }
        if (result.status === 429) throw new Error('Too many sign-in attempts. Try again in a minute.');
        throw new Error(msg || `Sign in failed (HTTP ${result.status}).`);
      }
      const data = result.data;
      if (data?.access_token && data?.refresh_token) {
        try { await supabase.auth.setSession({ access_token: data.access_token, refresh_token: data.refresh_token }); }
        catch { /* non-fatal */ }
      }
      return { user: data.user || null, session: data.access_token ? data : null };
    },

    async signOut() {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    },

    async requestPasswordReset(email) {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: resetRedirectUrl(),
      });
      if (error) throw error;
    },

    async updatePassword(newPassword) {
      const { data, error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      return { user: data.user };
    },

    async resendConfirmation(email) {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo: resetRedirectUrl().replace('/reset-password', '/login') },
      });
      if (error) throw error;
      return { ok: true };
    },

    onAuthStateChange(callback) {
      const { data } = supabase.auth.onAuthStateChange((event, session) => callback(event, session));
      return () => { try { data?.subscription?.unsubscribe(); } catch {} };
    },
  };
};

// ─── Public export ────────────────────────────────────────────────────
export const authClient = isSupabaseConfigured() ? buildSupabaseAuth() : mockAuth;
export const isSupabaseAuth = () => authClient.type === 'supabase';
